import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Language } from "@mui/icons-material";
import { toast } from "react-toastify";
import { format } from "date-fns";
import api from "../../../api";

const TYPE_LABELS = { mock_exam: "Mock exams", paper: "Past papers" };

const BATCH_OPTIONS = [
  { value: "all", label: "All of them" },
  { value: 25, label: "Next 25" },
  { value: 50, label: "Next 50" },
  { value: 100, label: "Next 100" },
];

/**
 * "Web check all": one-off web plagiarism run over every mock exam and past paper that has
 * not been web-checked yet. Already checked items are never searched again, so it can be run
 * in batches and resumed. The backend checks one item at a time (WebSimilarityBulkController).
 *
 * @param defaultTypes  types ticked when the dialog opens (the page it was opened from)
 * @param onFinished    called when a run finishes, so the list can refresh its chips
 */
const BulkWebCheckDialog = ({ open, onClose, defaultTypes = ["mock_exam", "paper"], onFinished }) => {
  const [summary, setSummary] = useState(null);
  const [types, setTypes] = useState(defaultTypes);
  const [batchSize, setBatchSize] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/web-similarity/bulk");
      setSummary(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load web check summary");
    }
  }, []);

  useEffect(() => {
    if (open) {
      setTypes(defaultTypes);
      load();
    } else {
      setSummary(null);
    }
  }, [open, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const batch = summary?.batch;
  const running = Boolean(batch && !batch.finished);

  // Poll while a run is in progress; tell the page once it finishes.
  useEffect(() => {
    if (!open || !running) return undefined;
    const t = setTimeout(async () => {
      await load();
    }, 5000);
    return () => clearTimeout(t);
  }, [open, running, summary, load]);

  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (running) setWasRunning(true);
    else if (wasRunning) {
      setWasRunning(false);
      onFinished?.();
    }
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = summary?.types || {};
  const selectedToCheck = types.reduce((n, t) => n + (counts[t]?.to_check || 0), 0);
  const runCount = batchSize === "all" ? selectedToCheck : Math.min(batchSize, selectedToCheck);
  const maxQueries = summary?.max_queries ?? 6;

  const toggleType = (t) => setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const handleStart = async () => {
    setBusy(true);
    try {
      await api.post("/admin/web-similarity/bulk", { types, limit: batchSize === "all" ? null : batchSize });
      toast.success(`Web check started for ${runCount} item${runCount === 1 ? "" : "s"}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to start the web check");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      await api.post("/admin/web-similarity/bulk/cancel");
      toast.info("Web check run cancelled. The item being checked right now still finishes.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  // While running, the run's own types (the checkboxes reset when the dialog reopens).
  const shownTypes = running ? batch.types : types;
  const remaining = (batch?.types || []).reduce((n, t) => n + (counts[t]?.to_check || 0) + (counts[t]?.pending || 0), 0);
  const runTotal = batch ? (batch.limit ?? batch.processed + remaining) : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Language color="action" />
        Web check all papers
      </DialogTitle>
      <DialogContent dividers>
        {!summary ? (
          <LinearProgress />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Searches the web for content from every mock exam and past paper that has not been web-checked yet.
              Items already checked are never searched again, so you can run it in batches and continue later.
            </Typography>

            {!summary.configured && (
              <Alert severity="warning">Web search is not set up. Add <code>SERPER_API_KEY</code> to the backend <code>.env</code>.</Alert>
            )}

            {/* Per type counts */}
            <Box>
              {Object.keys(TYPE_LABELS).filter((t) => counts[t]).map((t) => {
                const c = counts[t];
                const checked = c.done + c.skipped;
                return (
                  <Box key={t} sx={{ mb: 1.25 }}>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={shownTypes.includes(t)} onChange={() => toggleType(t)} disabled={running} />}
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {TYPE_LABELS[t]}: {c.to_check} to check
                          <Typography component="span" variant="caption" color="text.secondary">
                            {" "}· {checked} of {c.total} already checked{c.failed ? ` · ${c.failed} failed (will retry)` : ""}
                          </Typography>
                        </Typography>
                      }
                    />
                    <LinearProgress variant="determinate" value={c.total ? (100 * checked) / c.total : 0} sx={{ height: 5, borderRadius: 3, ml: 4 }} />
                  </Box>
                );
              })}
            </Box>

            {running ? (
              <Alert severity="info" icon={<CircularProgress size={18} />}>
                <strong>Running:</strong> {batch.processed} checked so far
                {batch.limit ? ` of ${batch.limit}` : ""}. One item is checked at a time (about 20 seconds each).
                You can close this dialog; it keeps running in the background.
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  Not moving? A queue worker must be running (<code>php artisan queue:work --queue=question-bank-imports,default</code>).
                </Typography>
                {runTotal > 0 && (
                  <LinearProgress variant="determinate" value={Math.min(100, (100 * batch.processed) / runTotal)} sx={{ mt: 1, height: 6, borderRadius: 3 }} />
                )}
              </Alert>
            ) : (
              <>
                {batch?.finished && (
                  <Alert severity={batch.stopped_reason && !batch.stopped_reason.startsWith("Cancelled") ? "error" : "success"}>
                    Last run: {batch.processed} checked
                    {batch.finished_at ? ` · finished ${format(new Date(batch.finished_at), "d MMM yyyy HH:mm")}` : ""}
                    {batch.stopped_reason ? `. ${batch.stopped_reason}` : ""}
                  </Alert>
                )}
                {selectedToCheck > 0 ? (
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>How many</InputLabel>
                      <Select value={batchSize} label="How many" onChange={(e) => setBatchSize(e.target.value)}>
                        {BATCH_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                      {runCount} item{runCount === 1 ? "" : "s"} · up to <strong>{(runCount * maxQueries).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong> web
                      searches (fewer if some are cached) · about {Math.max(1, Math.round((runCount * 20) / 60))} min
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="success">Everything selected has already been web-checked.</Alert>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {running ? (
          <Button color="error" onClick={handleCancel} disabled={busy} sx={{ textTransform: "none", mr: "auto" }}>
            Cancel run
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Language />}
            onClick={handleStart}
            disabled={busy || !summary?.configured || !types.length || selectedToCheck === 0}
            sx={{ textTransform: "none", mr: "auto" }}
          >
            Start web check
          </Button>
        )}
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkWebCheckDialog;
